import React from "react";
import styles from "./VoiceOutput.module.css";
import { SpeakerOnIcon, SpeakerOffIcon, SpinnerIcon } from "./icons";
import { useUiCopy } from "../../i18n/useUiCopy";

interface VoiceOutputProps {
  /** Whether TTS audio is enabled */
  audioEnabled: boolean;
  /** Whether audio chunks are currently being processed */
  isAudioLoading?: boolean;
  /** Whether the user has interacted with the page (for autoplay policy compliance) */
  userHasInteracted: boolean;
  /** Callback to enable audio playback (handles user interaction) */
  onEnableAudioPlayback: () => Promise<void>;
  /** Callback when audio enabled state changes */
  onAudioEnabledChange: (enabled: boolean) => void;
  /** Callback to process audio chunks for testing */
  onProcessAudioChunk?: (chunk: any) => void;
  /** Additional CSS class name */
  className?: string;
}

/**
 * VoiceOutput component for TTS (Text-to-Speech) audio controls
 *
 * Provides:
 * - TTS enable/disable toggle
 * - User interaction detection for browser autoplay compliance
 * - Development mode test audio functionality
 */
export const VoiceOutput: React.FC<VoiceOutputProps> = ({
  audioEnabled,
  isAudioLoading = false,
  userHasInteracted,
  onEnableAudioPlayback,
  onAudioEnabledChange,
  onProcessAudioChunk,
  className = "",
}) => {
  const copy = useUiCopy();
  if (process.env.REACT_APP_VOICE_TTS_ENABLED?.toLowerCase() !== "true") {
    return null;
  }

  const handleToggleClick = async () => {
    // If we're enabling audio, ensure user interaction is recorded
    if (!audioEnabled && !userHasInteracted) {
      await onEnableAudioPlayback();
    }

    onAudioEnabledChange(!audioEnabled);
  };

  const getButtonTitle = () => {
    if (isAudioLoading) {
      return `${copy.processingAudio}...`;
    }
    if (audioEnabled) {
      return copy.clickToDisableTts;
    }
    return copy.clickToEnableTts;
  };

  const getButtonIcon = () => {
    if (isAudioLoading) {
      return <SpinnerIcon width={20} height={20} className={styles.spinner} />;
    }
    if (audioEnabled) {
      return <SpeakerOnIcon width={20} height={20} />;
    }
    return <SpeakerOffIcon width={20} height={20} />;
  };

  // Button styling based on enabled state
  const buttonClassName = `${styles.button} ${audioEnabled ? styles.enabled : styles.disabled}`;

  const buttonAriaLabel = isAudioLoading
    ? copy.processingAudio
    : audioEnabled
    ? copy.disableTts
    : copy.enableTts;

  return (
    <div className={`${styles.container} ${className}`}>
      <button
        aria-label={buttonAriaLabel}
        onClick={handleToggleClick}
        className={buttonClassName}
        title={getButtonTitle()}
        type="button"
        // disabled={isAudioLoading}
      >
        {getButtonIcon()}
      </button>
    </div>
  );
};
